import { Modal, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { ExternalIntegrationsClient, GoogleAdsIntegration } from 'api/clients/ExternalIntegrationsClient'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useMutation, useQueryClient } from 'react-query'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import Styles from './GoogleAdsExistingCampaignsModal.module.scss'

interface Props {
  integration: GoogleAdsIntegration
  onClose: () => void
}

export const GoogleAdsExistingCampaignsModal = ({ integration, onClose }: Props) => {
  const { closeModal } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const campaigns = integration.additional_data?.existing_campaigns_summary ?? []

  const { mutateAsync: importCampaigns, isLoading } = useMutation(() =>
    ExternalIntegrationsClient.importExistingCampaigns({ workspaceId, id: integration.id }),
  )

  const handleAddExisting = async () => {
    try {
      await importCampaigns()
      queryClient.invalidateQueries(['google_ads_campaigns', workspaceId])
      closeModal()
      onClose()
    } catch {
      showToastMessage('Failed to import campaigns. Please try again.', 'error')
    }
  }

  const handleStartFresh = () => {
    closeModal()
    onClose()
  }

  return (
    <Modal.Root
      size="sm"
      onPressOutside={() => {}}
      onClose={() => {}}
      aria-label="Existing Google Ads campaigns detected"
    >
      <Modal.Header title="Existing Google Ads campaigns detected" />
      <Modal.Content>
        <div className={Styles.content}>
          <Text>
            We found {campaigns.length} active campaign{campaigns.length !== 1 ? 's' : ''} in your Google Ads account.
            Would you like Blaze to manage them, or start fresh with new campaigns?
          </Text>
          <div className={Styles.campaignTable}>
            <div className={Styles.tableHeader}>
              <Text variant="metadata" color="var(--dark-40)">
                Campaign name
              </Text>
            </div>
            {campaigns.map((campaign) => (
              <div key={campaign.id} className={Styles.campaignRow}>
                <Text variant="secondary" color="var(--dark-90)">
                  {campaign.name}
                </Text>
                <Text variant="metadata" color="var(--dark-40)">
                  Search
                </Text>
              </div>
            ))}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton variant="tertiary" onPress={handleStartFresh} isDisabled={isLoading}>
            Start Fresh
          </Modal.FooterButton>
          <Modal.FooterButton variant="primary" onPress={handleAddExisting} isDisabled={isLoading}>
            {isLoading ? 'Adding...' : 'Add Existing Campaigns'}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
